// ArchviewVR dashboard: hash routes, sign-in gate and studio context.
import './style.css';
import { currentSession, onAuthChange, handleAuthRedirect } from './supa';
import { myStudios, checkAdmin, type Studio } from './data';
import { authView, studioSetupView } from './views/auth';
import { projectsView, newProjectView } from './views/projects';
import { uploadView } from './views/upload';
import { checkView } from './views/check';
import { projectView } from './views/project';
import { adminView } from './views/admin';
import { setupView } from './views/setup';
import { tutorialEditView } from './views/tutorial-edit';
import { projectAnalyticsView } from './views/analytics';
import { brandView } from './views/brand';
import { enquiriesView } from './views/enquiries';
import { toast, html, setAdminCog, type SetupSection } from './ui';

const app = document.getElementById('app')!;
let studio: Studio | null = null;
let recovering = false;
let isAdmin = false;

async function loadStudio(): Promise<Studio | null> {
  const m = await myStudios();
  return m[0]?.studios ?? null;
}

async function route(): Promise<void> {
  const path = location.hash.replace(/^#/, '') || '/';
  window.scrollTo(0, 0);

  if (!currentSession()) {
    studio = null;
    if (path === '/signup') authView(app, 'signup');
    else if (path === '/reset') authView(app, 'reset');
    else authView(app, 'signin');
    return;
  }
  if (recovering) { authView(app, 'newpass'); return; }
  if (!studio) {
    app.innerHTML = '<div class="loading-page">Loading your studio</div>';
    try { [studio, isAdmin] = await Promise.all([loadStudio(), checkAdmin()]); setAdminCog(isAdmin); }
    catch (e) { app.innerHTML = html`<div class="loading-page">Couldn’t reach ArchviewVR: ${e instanceof Error ? e.message : ''}</div>`.html; return; }
    if (!studio) { studioSetupView(app, () => { void route(); }); return; }
  }
  const s = studio;
  let m: RegExpMatchArray | null;
  if (path === '/' || path === '/signin' || path === '/signup') await projectsView(app, s);
  else if (path === '/new') newProjectView(app, s);
  else if ((m = path.match(/^\/p\/([0-9a-f-]{36})\/analytics(?:\/([a-z]+))?$/))) await projectAnalyticsView(app, s, m[1], m[2]);
  else if ((m = path.match(/^\/p\/([0-9a-f-]{36})\/brand(?:\/([a-z]+))?$/))) await brandView(app, s, m[1], m[2]);
  else if ((m = path.match(/^\/p\/([0-9a-f-]{36})\/enquiries$/))) await enquiriesView(app, s, m[1]);
  else if (path === '/admin' && isAdmin) await adminView(app, s);
  else if (path === '/admin/tutorial' && isAdmin) await tutorialEditView(app, s);
  else if ((m = path.match(/^\/p\/([0-9a-f-]{36})\/upload$/))) await uploadView(app, m[1]);
  else if ((m = path.match(/^\/p\/([0-9a-f-]{36})\/check$/))) await checkView(app, m[1]);
  else if ((m = path.match(/^\/p\/([0-9a-f-]{36})\/setup(?:\/(start|rooms|hotspots|alts|levels|options))?$/))) await setupView(app, s, m[1], (m[2] as SetupSection | undefined) ?? 'rooms');
  else if ((m = path.match(/^\/p\/([0-9a-f-]{36})$/))) await projectView(app, s, m[1]);
  else location.hash = '#/';
}

async function boot(): Promise<void> {
  // tour addresses belong to the viewer; never show the dashboard inside a tour frame
  if (location.pathname.startsWith('/t/') || location.pathname === '/t') {
    app.innerHTML = '<div class="loading-page">This tour could not be loaded.</div>';
    return;
  }
  try {
    const type = await handleAuthRedirect();
    if (type === 'recovery') recovering = true;
    if (type === 'signup') toast('Email confirmed. Welcome to ArchviewVR.');
  } catch (e) {
    toast(e instanceof Error ? e.message : 'That link didn’t work. Try signing in.', 'error', 7000);
  }
  onAuthChange((s) => { if (!s) { studio = null; isAdmin = false; setAdminCog(false); recovering = false; location.hash = '#/signin'; void route(); } });
  addEventListener('hashchange', () => {
    if (recovering && currentSession()) recovering = false; // leaving the new-password screen
    void route().catch(showError);
  });
  await route().catch(showError);
}

function showError(e: unknown): void {
  console.error(e);
  toast(e instanceof Error ? e.message : 'Something went wrong', 'error', 7000);
}

void boot();
